import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Fade,
  Grid,
  TextField,
  InputAdornment,
  useTheme,
  alpha,
  Tooltip,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Fab,
  Stack,
  Divider,
  ButtonGroup,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  Search as SearchIcon,
  Hotel as HotelIcon,
  TrendingUp,
  TrendingDown,
  BusinessCenter,
  Apartment,
  MonetizationOn,
  AddBusiness,
  Assessment,
  TrendingUpOutlined,
  StarOutline,
  MoreVert,
  Visibility,
  Edit,
  FilterList,
  ViewModule,
  ViewList,
  Analytics,
  Speed,
  AccountBalance,
  LocationOn,
  CalendarToday,
  Notifications,
  Settings,
  Add,
  Refresh,
  Download,
  Share,
  PieChart,
  BarChart,
  ShowChart,
  Map,
  Description,
  Upload,
} from '@mui/icons-material';
import { api } from '@services';
import { StatsCard, DataTable } from '@components/common';
import { Column } from '@components/common/DataTable';
import { useNavigate } from 'react-router-dom';

// Define types for hotels and deals
interface Hotel {
  id: number;
  name: string;
  location: string;
  address?: string;
  city?: string;
  state?: string;
  rooms: number;
  lastUpdated?: string;
  updated_at?: string;
  value?: number;
  estimated_value?: number;
  status?: string;
  type?: string;
  property_type?: string;
  yearBuilt?: number;
  year_built?: number;
  occupancy_rate?: number;
}

interface Deal {
  id: number;
  name: string;
  title?: string;
  value: number;
  deal_value?: number;
  client: string;
  client_name?: string;
  status: string;
  date?: string;
  due_date?: string;
  progress: number;
  completion_percentage?: number;
  created_at?: string;
}

interface QuickAction {
  title: string;
  subtitle: string;
  icon: React.ElementType;
  color: string;
  action: () => void;
}

const ValuationDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hotelsLoading, setHotelsLoading] = useState<boolean>(true);
  const [dealsLoading, setDealsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [refreshing, setRefreshing] = useState(false);

  // Create filtered hotels and deals based on search term and filter
  const filteredHotels = useMemo(() => {
    let filtered = hotels;

    // Apply search filter
    if (searchTerm.trim()) {
      const searchTermLower = searchTerm.toLowerCase();
      filtered = filtered.filter(hotel =>
        hotel.name.toLowerCase().includes(searchTermLower) ||
        (hotel.location && hotel.location.toLowerCase().includes(searchTermLower)) ||
        (hotel.type && hotel.type.toLowerCase().includes(searchTermLower))
      );
    }

    // Apply status filter
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(hotel =>
        hotel.status?.toLowerCase() === selectedFilter.toLowerCase()
      );
    }

    return filtered;
  }, [hotels, searchTerm, selectedFilter]);

  const filteredDeals = useMemo(() => {
    if (!searchTerm.trim()) return deals;

    const searchTermLower = searchTerm.toLowerCase();
    return deals.filter(deal =>
      deal.name.toLowerCase().includes(searchTermLower) ||
      (deal.client && deal.client.toLowerCase().includes(searchTermLower)) ||
      (deal.status && deal.status.toLowerCase().includes(searchTermLower))
    );
  }, [deals, searchTerm]);

  useEffect(() => {
    // Fetch hotels and deals concurrently
    const fetchData = async () => {
      try {
        // Add cache-busting parameters to ensure fresh data
        const cacheBuster = `?_t=${Date.now()}&_r=${Math.random()}`;
        const hotelsPromise = api.get(`/hotels${cacheBuster}`);
        const dealsPromise = api.get(`/deals${cacheBuster}`);

        const [hotelsResponse, dealsResponse] = await Promise.all([
          hotelsPromise,
          dealsPromise
        ]);

        // Process hotel data
        const processedHotels = Array.isArray(hotelsResponse.data)
          ? hotelsResponse.data.map((hotel: any) => ({
              id: hotel.id,
              name: hotel.name,
              location: hotel.city && hotel.state ? `${hotel.city}, ${hotel.state}` :
                      (hotel.address || 'Location unavailable'),
              rooms: hotel.num_rooms || hotel.rooms || 0,
              value: parseFloat(hotel.estimated_value) || 0,
              status: hotel.status || 'active',
              type: hotel.property_type || 'Unknown',
              yearBuilt: hotel.year_built || null,
              lastUpdated: hotel.updated_at || hotel.created_at || new Date().toISOString(),
              occupancy_rate: hotel.occupancy_rate || 0,
            }))
          : [];

        console.log('🏨 Raw API response:', hotelsResponse.data);
        console.log('🏨 Processed hotels data:', processedHotels);
        console.log('💰 Hotels values:', processedHotels.map(h => ({ name: h.name, value: h.value, type: typeof h.value })));
        setHotels(processedHotels);
        setHotelsLoading(false);

        // Process deal data
        const processedDeals = Array.isArray(dealsResponse.data)
          ? dealsResponse.data.map((deal: any) => ({
              id: deal.id,
              name: deal.title || deal.name || `Deal #${deal.id}`,
              value: deal.deal_value || 0,
              client: deal.client_name || deal.client || 'Unknown Client',
              status: deal.status || 'open',
              date: deal.due_date || deal.created_at || new Date().toISOString(),
              progress: deal.completion_percentage || 0
            }))
          : [];

        setDeals(processedDeals);
        setDealsLoading(false);
      } catch (error: any) {
        console.error('Error fetching dashboard data:', error);
        setError(`Failed to load dashboard data: ${error.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatStatus = (status: string) => {
    const statusMap: Record<string, { color: string, label: string }> = {
      active: { color: '#10B981', label: 'Active' },
      pending: { color: '#F59E0B', label: 'Pending' },
      closed: { color: '#6B7280', label: 'Closed' },
      open: { color: '#2563EB', label: 'Open' },
      negotiating: { color: '#8B5CF6', label: 'Negotiating' },
      completed: { color: '#10B981', label: 'Completed' },
    };

    const statusInfo = statusMap[status.toLowerCase()] || { color: '#6B7280', label: status };

    return (
      <Chip
        label={statusInfo.label}
        size="small"
        sx={{
          backgroundColor: `${statusInfo.color}20`,
          color: statusInfo.color,
          fontWeight: 600,
          '& .MuiChip-label': {
            px: 1.5,
          }
        }}
      />
    );
  };

  const calculateMetrics = () => {
    console.log('📊 Calculating metrics for hotels:', hotels.map(h => ({ name: h.name, value: h.value, type: typeof h.value })));
    const totalPropertyValue = hotels.reduce((total, hotel) => total + (hotel.value || 0), 0);
    console.log('💵 Total Property Value calculated:', totalPropertyValue);
    const totalDealValue = deals.reduce((total, deal) => total + (deal.value || 0), 0);

    let totalOccupancy = 0;
    let occupancyCount = 0;
    let totalRevPAR = 0;
    hotels.forEach(hotel => {
      if (hotel.occupancy_rate) {
        totalOccupancy += hotel.occupancy_rate;
        occupancyCount++;
        // Calculate estimated RevPAR (simplified calculation)
        const estimatedADR = (hotel.value || 0) / (hotel.rooms || 1) / 365 * 12; // Rough ADR estimate
        totalRevPAR += hotel.occupancy_rate * estimatedADR;
      }
    });
    const avgOccupancy = occupancyCount > 0 ? totalOccupancy / occupancyCount : 0.752;
    const avgRevPAR = occupancyCount > 0 ? totalRevPAR / occupancyCount : 165;

    const activeDeals = deals.filter(deal =>
      deal.status?.toLowerCase() === 'open' ||
      deal.status?.toLowerCase() === 'negotiating' ||
      deal.status?.toLowerCase() === 'pending'
    ).length;
    const completedDeals = deals.filter(deal => deal.status?.toLowerCase() === 'completed').length;
    const dealCompletionRate = deals.length > 0 ? (completedDeals / deals.length) * 100 : 0;

    // Calculate estimated NOI (Net Operating Income) - simplified
    const estimatedNOI = totalPropertyValue * 0.08 / 12; // 8% NOI annually

    return {
      totalPropertyValue,
      totalDealValue,
      avgOccupancy,
      avgRevPAR,
      activeDeals,
      dealCompletionRate,
      estimatedNOI,
      occupancyTarget: 0.78,
    };
  };

  const metrics = calculateMetrics();

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterSelect = (filter: string) => {
    setSelectedFilter(filter);
    handleFilterClose();
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Actually refresh the data
      const cacheBuster = `?_t=${Date.now()}&_r=${Math.random()}`;
      const hotelsPromise = api.get(`/hotels${cacheBuster}`);
      const dealsPromise = api.get(`/deals${cacheBuster}`);

      const [hotelsResponse, dealsResponse] = await Promise.all([
        hotelsPromise,
        dealsPromise
      ]);

      // Process hotel data
      const processedHotels = Array.isArray(hotelsResponse.data)
        ? hotelsResponse.data.map((hotel: any) => ({
            id: hotel.id,
            name: hotel.name,
            location: hotel.city && hotel.state ? `${hotel.city}, ${hotel.state}` :
                    (hotel.address || 'Location unavailable'),
            rooms: hotel.num_rooms || hotel.rooms || 0,
            value: parseFloat(hotel.estimated_value) || 0,
            status: hotel.status || 'active',
            type: hotel.property_type || 'Unknown',
            yearBuilt: hotel.year_built || null,
            lastUpdated: hotel.updated_at || hotel.created_at || new Date().toISOString(),
            occupancy_rate: hotel.occupancy_rate || 0,
          }))
        : [];

      console.log('🔄 Refresh - Raw API response:', hotelsResponse.data);
      console.log('🔄 Refresh - Processed hotels:', processedHotels);

      setHotels(processedHotels);

      // Process deal data
      const processedDeals = Array.isArray(dealsResponse.data)
        ? dealsResponse.data.map((deal: any) => ({
            id: deal.id,
            name: deal.title || deal.name || `Deal #${deal.id}`,
            value: deal.deal_value || 0,
            client: deal.client_name || deal.client || 'Unknown Client',
            status: deal.status || 'open',
            date: deal.due_date || deal.created_at || new Date().toISOString(),
            progress: deal.completion_percentage || 0
          }))
        : [];

      setDeals(processedDeals);
    } catch (error: any) {
      console.error('Error refreshing dashboard data:', error);
      setError(`Failed to refresh data: ${error.message || 'Unknown error'}`);
    } finally {
      setRefreshing(false);
    }
  };

  const handleHotelView = (hotel: Hotel) => {
    navigate(`/hotels/${hotel.id}`);
  };

  const handleDealView = (deal: Deal) => {
    navigate(`/deals/${deal.id}`);
  };

  // Quick actions
  const quickActions: QuickAction[] = [
    {
      title: 'Add Hotel',
      subtitle: 'New property',
      icon: AddBusiness,
      color: theme.palette.primary.main,
      action: () => navigate('/add-hotel')
    },
    {
      title: 'Run Analysis',
      subtitle: 'Market research',
      icon: Analytics,
      color: theme.palette.info.main,
      action: () => navigate('/market-research')
    },
    {
      title: 'Create Deal',
      subtitle: 'New opportunity',
      icon: BusinessCenter,
      color: theme.palette.success.main,
      action: () => navigate('/deals/create')
    },
    {
      title: 'Generate Report',
      subtitle: 'Portfolio summary',
      icon: Description,
      color: theme.palette.warning.main,
      action: () => navigate('/reports')
    }
  ];

  // Check if this is a new user
  const isNewUser = hotels.length === 0 && !hotelsLoading;

  // Onboarding Component for new users
  const OnboardingSection = () => (
    <Fade in timeout={800}>
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          {/* Welcome Hero Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                minHeight: 280,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 200,
                  height: 200,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${alpha('#ffffff', 0.1)} 0%, transparent 70%)`,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -30,
                  left: -30,
                  width: 150,
                  height: 150,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${alpha('#ffffff', 0.05)} 0%, transparent 70%)`,
                }}
              />
              <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      mr: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    <HotelIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                      Welcome to Valuate AI
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      Your intelligent hotel valuation platform
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 4, opacity: 0.8, maxWidth: 600, lineHeight: 1.6 }}>
                  Transform your hotel investment strategy with AI-powered valuations, comprehensive market analysis,
                  and real-time insights. Start by adding your first property to unlock professional-grade analytics.
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/add-hotel')}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: 3,
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                    startIcon={<AddBusiness />}
                  >
                    Add Your First Hotel
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/market-research')}
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      color: 'white',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: 3,
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.8)',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                    startIcon={<Analytics />}
                  >
                    Explore Features
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Feature Cards */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 2, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                  <Assessment sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  AI-Powered Valuations
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Generate precise property valuations using advanced machine learning algorithms, market comparables,
                  and real-time data analysis for informed investment decisions.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 2, backgroundColor: alpha(theme.palette.success.main, 0.1) }}>
                  <TrendingUpOutlined sx={{ fontSize: 32, color: theme.palette.success.main }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Market Intelligence
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Access comprehensive market data, competitor analysis, and trend forecasting to identify
                  high-potential investment opportunities before the competition.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', borderRadius: 3, boxShadow: 3, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 2, backgroundColor: alpha(theme.palette.warning.main, 0.1) }}>
                  <StarOutline sx={{ fontSize: 32, color: theme.palette.warning.main }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Portfolio Analytics
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Monitor portfolio performance with interactive dashboards, automated reports, and real-time
                  alerts to optimize your hotel investment strategy.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );

    // Enhanced Hotel Card Component with Professional Analyst Focus
  const HotelCard: React.FC<{ hotel: Hotel }> = ({ hotel }) => {
    const occupancyRate = hotel.occupancy_rate || 0;
    const estimatedADR = (hotel.value || 0) / (hotel.rooms || 1) / 365 * 12; // Rough ADR estimate
    const estimatedRevPAR = occupancyRate * estimatedADR;
    const isOccupancyLow = occupancyRate > 0 && occupancyRate < 0.75;
    const occupancyTarget = 0.78;
    const occupancyTrend = occupancyRate > occupancyTarget ? 'up' : occupancyRate < occupancyTarget ? 'down' : 'neutral';

    return (
      <Card
        sx={{
          height: 'auto',
          borderRadius: 2,
          boxShadow: 1,
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 3,
            borderColor: alpha(theme.palette.primary.main, 0.3),
          },
        }}
        onClick={() => handleHotelView(hotel)}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {/* Header with Property Image and Alert */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
            <Box
              sx={{
                width: 60,
                height: 40,
                borderRadius: 1,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5,
                flexShrink: 0,
              }}
            >
              <HotelIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2, flex: 1 }} noWrap>
                  {hotel.name}
                  {isOccupancyLow && (
                    <Box
                      component="span"
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: 'warning.main',
                        display: 'inline-block',
                        ml: 1,
                      }}
                    />
                  )}
                </Typography>
                <IconButton
                  size="small"
                  sx={{ p: 0.25, opacity: 0.6, '&:hover': { opacity: 1 } }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Handle menu actions
                  }}
                >
                  <MoreVert sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.25 }}>
                <LocationOn sx={{ fontSize: 12, mr: 0.25 }} />
                {hotel.location}
              </Typography>
            </Box>
          </Box>

          {/* Key Metrics Row */}
          <Grid container spacing={2} sx={{ mb: 1.5 }}>
            <Grid item xs={6}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                  Occupancy (TTM)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: isOccupancyLow ? 'warning.main' : occupancyRate >= occupancyTarget ? 'success.main' : 'text.primary',
                      fontSize: '1rem',
                      mr: 0.5
                    }}
                  >
                    {occupancyRate > 0 ? `${Math.round(occupancyRate * 100)}%` : 'N/A'}
                  </Typography>
                  {occupancyRate > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {occupancyTrend === 'up' && <TrendingUp sx={{ fontSize: 12, color: 'success.main' }} />}
                      {occupancyTrend === 'down' && <TrendingDown sx={{ fontSize: 12, color: 'error.main' }} />}
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                  RevPAR
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1rem' }}>
                  {occupancyRate > 0 ? `$${Math.round(estimatedRevPAR)}` : 'N/A'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Property Details */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                {hotel.rooms} Rooms
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {hotel.type || 'Full Service'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.success.main, fontSize: '0.9rem' }}>
                {formatCurrency(hotel.value || 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Est. Value
              </Typography>
            </Box>
          </Box>

          {/* Status and Quick Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {formatStatus(hotel.status || 'active')}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                sx={{
                  p: 0.5,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/hotels/${hotel.id}`);
                }}
              >
                <Visibility sx={{ fontSize: 14, color: 'primary.main' }} />
              </IconButton>
              <IconButton
                size="small"
                sx={{
                  p: 0.5,
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  '&:hover': { backgroundColor: alpha(theme.palette.secondary.main, 0.2) }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/valuations/new?hotelId=${hotel.id}`);
                }}
              >
                <Assessment sx={{ fontSize: 14, color: 'secondary.main' }} />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.background.default, 1)} 100%)`,
        minHeight: '100vh',
        pb: 4,
        pt: 1,
      }}
    >
      {/* Simplified Header - Only for new users */}
      {isNewUser && (
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backdropFilter: 'blur(10px)',
            px: { xs: 2, sm: 3, md: 4 },
            py: 3,
            mt: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              }}
            >
              <TimelineIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Welcome to Valuate
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Get started with your first property
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: isNewUser ? 2 : 3 }}>
        {error && (
          <Fade in={!!error}>
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 3,
                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.2)}`
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          </Fade>
        )}

        {/* Show onboarding for new users, dashboard for existing users */}
        {isNewUser ? (
          <OnboardingSection />
        ) : (
          <>
            {/* Dashboard Header with Search and Actions */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    }}
                  >
                    <TimelineIcon />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Portfolio Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {`${hotels.length} properties • ${deals.length} deals`}
                    </Typography>
                  </Box>
                </Box>

                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    variant="outlined"
                    placeholder="Search properties, deals, reports, contacts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="medium"
                    sx={{
                      width: { xs: '100%', sm: '400px' },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: theme.palette.background.paper,
                        transition: 'all 0.2s',
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`,
                        '&:hover': {
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
                          borderColor: theme.palette.primary.main,
                        }
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <ButtonGroup variant="outlined" size="small">
                    <IconButton
                      onClick={handleFilterClick}
                      color={selectedFilter !== 'all' ? 'primary' : 'default'}
                    >
                      <FilterList />
                    </IconButton>
                    <IconButton
                      onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                      color="default"
                    >
                      {viewMode === 'grid' ? <ViewList /> : <ViewModule />}
                    </IconButton>
                    <IconButton
                      onClick={handleRefresh}
                      disabled={refreshing}
                    >
                      <Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                    </IconButton>
                  </ButtonGroup>
                </Stack>
              </Box>
            </Box>

                        {/* Portfolio Health KPIs - Compact */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} lg={3}>
                <Card
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': { boxShadow: 2 }
                  }}
                  onClick={() => navigate('/hotels')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      PORTFOLIO VALUE
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1 }}>
                    {formatCurrency(metrics.totalPropertyValue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {hotels.length} Properties
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6} lg={3}>
                <Card
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': { boxShadow: 2 }
                  }}
                  onClick={() => navigate('/deals')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      DEAL PIPELINE
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1 }}>
                    {formatCurrency(metrics.totalDealValue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {metrics.activeDeals} Active Deals
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6} lg={3}>
                <Card
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': { boxShadow: 2 }
                  }}
                  onClick={() => navigate('/analytics')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      OCCUPANCY (TTM)
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1 }}>
                    {Math.round(metrics.avgOccupancy * 100)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Target: {Math.round(metrics.occupancyTarget * 100)}%
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6} lg={3}>
                <Card
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': { boxShadow: 2 }
                  }}
                  onClick={() => navigate('/financials')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      MONTHLY NOI
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1 }}>
                    {formatCurrency(metrics.estimatedNOI)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Estimated
                  </Typography>
                </Card>
              </Grid>
            </Grid>

                        {/* Your Next Steps & AI Insights */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Your Next Steps */}
              <Grid item xs={12} md={8}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                    Your Next Steps
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 3,
                          }
                        }}
                        onClick={() => navigate('/add-hotel')}
                      >
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                          }}
                        >
                          <AddBusiness sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          Import Hotel
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 3,
                          }
                        }}
                        onClick={() => navigate('/market-research')}
                      >
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                          }}
                        >
                          <Analytics sx={{ color: theme.palette.info.main, fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          Run Analysis
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 3,
                          }
                        }}
                        onClick={() => navigate('/deals/create')}
                      >
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                          }}
                        >
                          <BusinessCenter sx={{ color: theme.palette.success.main, fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          Create Deal
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* AI Insights */}
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                    AI Insights
                  </Typography>

                  {/* Primary Insight Card */}
                  <Card sx={{ p: 3, borderRadius: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          backgroundColor: alpha(theme.palette.warning.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        <Assessment sx={{ color: theme.palette.warning.main, fontSize: 18 }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Valuation Opportunity
                        </Typography>
                        <Typography variant="body2" sx={{ lineHeight: 1.5, color: 'text.secondary' }}>
                          {hotels.filter(h => !h.value || h.value === 0).length} properties need valuation analysis
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => navigate('/hotels?filter=unvalued')}
                      sx={{ py: 1.5, fontWeight: 600 }}
                    >
                      View Unvalued Properties
                    </Button>
                  </Card>

                  {/* Secondary Insights */}
                  <Card sx={{ p: 3, borderRadius: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          backgroundColor: alpha(theme.palette.info.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        <TrendingDown sx={{ color: theme.palette.info.main, fontSize: 18 }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Occupancy Alert
                        </Typography>
                        <Typography variant="body2" sx={{ lineHeight: 1.5, color: 'text.secondary' }}>
                          {hotels.filter(h => h.occupancy_rate && h.occupancy_rate < 0.75).length} properties below 75% occupancy
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => navigate('/analytics')}
                      sx={{ py: 1.5, fontWeight: 600 }}
                    >
                      Review Performance
                    </Button>
                  </Card>

                  {/* Action Links */}
                  <Stack spacing={1}>
                    <Button
                      variant="text"
                      startIcon={<Upload />}
                      onClick={() => navigate('/documents')}
                      sx={{ fontWeight: 600, justifyContent: 'flex-start' }}
                    >
                      Import New Properties
                    </Button>
                    <Button
                      variant="text"
                      startIcon={<Download />}
                      onClick={() => navigate('/reports')}
                      sx={{ fontWeight: 600, justifyContent: 'flex-start' }}
                    >
                      Export Portfolio Report
                    </Button>
                  </Stack>
                </Box>
              </Grid>
            </Grid>

            {/* Hotel Portfolio */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Hotel Portfolio
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton size="small" color={selectedFilter !== 'all' ? 'primary' : 'default'}>
                    <FilterList />
                  </IconButton>
                  <IconButton size="small">
                    <MoreVert />
                  </IconButton>
                </Stack>
              </Box>

              {loading || hotelsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={40} />
                </Box>
              ) : (
                <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  {/* Table Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2.5,
                      backgroundColor: alpha(theme.palette.primary.main, 0.02),
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      fontWeight: 600,
                    }}
                  >
                    {/* Hotel Name Column Header */}
                    <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Property
                      </Typography>
                    </Box>

                    {/* Occupancy Column Header */}
                    <Box sx={{ textAlign: 'center', minWidth: 80, mx: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Occupancy
                      </Typography>
                    </Box>

                    {/* ADR Column Header */}
                    <Box sx={{ textAlign: 'center', minWidth: 80, mx: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        ADR
                      </Typography>
                    </Box>

                    {/* RevPAR Column Header */}
                    <Box sx={{ textAlign: 'center', minWidth: 80, mx: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        RevPAR
                      </Typography>
                    </Box>

                    {/* Status Column Header */}
                    <Box sx={{ textAlign: 'right', minWidth: 140 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Status
                      </Typography>
                    </Box>
                  </Box>

                  {filteredHotels.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <HotelIcon sx={{ fontSize: 60, mb: 2, opacity: 0.3, color: 'text.secondary' }} />
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        No hotels found
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                        {searchTerm ? 'Try adjusting your search criteria' : 'Add your first hotel to get started'}
                      </Typography>
                      {!searchTerm && (
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => navigate('/add-hotel')}
                        >
                          Add Your First Hotel
                        </Button>
                      )}
                    </Box>
                  ) : (
                    filteredHotels.map((hotel, index) => {
                      const occupancyRate = hotel.occupancy_rate || 0;
                      const estimatedADR = (hotel.value || 0) / (hotel.rooms || 1) / 365 * 12;
                      const estimatedRevPAR = occupancyRate * estimatedADR;
                      const hasValuation = hotel.value && hotel.value > 0;

                      return (
                        <Box
                          key={hotel.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 2.5,
                            borderBottom: index < filteredHotels.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.02),
                            }
                          }}
                          onClick={() => handleHotelView(hotel)}
                        >
                          {/* Hotel Icon */}
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1,
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                            }}
                          >
                            <HotelIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                          </Box>

                          {/* Hotel Name & Location */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.25 }}>
                              {hotel.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              {hotel.location}
                            </Typography>
                          </Box>

                          {/* Occupancy */}
                          <Box sx={{ textAlign: 'center', minWidth: 80, mx: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0 }}>
                              {occupancyRate > 0 ? `${Math.round(occupancyRate * 100)}%` : '-'}
                            </Typography>
                          </Box>

                          {/* ADR */}
                          <Box sx={{ textAlign: 'center', minWidth: 80, mx: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0 }}>
                              {estimatedADR > 0 ? `$${Math.round(estimatedADR)}` : '-'}
                            </Typography>
                          </Box>

                          {/* RevPAR */}
                          <Box sx={{ textAlign: 'center', minWidth: 80, mx: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0 }}>
                              {estimatedRevPAR > 0 ? `$${Math.round(estimatedRevPAR)}` : '-'}
                            </Typography>
                          </Box>

                          {/* Status */}
                          <Box sx={{ textAlign: 'right', minWidth: 140 }}>
                            {!hasValuation ? (
                              <Chip
                                label="Valuation Pending"
                                size="small"
                                sx={{
                                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                                  color: 'success.main',
                                  fontWeight: 600,
                                }}
                              />
                            ) : occupancyRate > 0 && occupancyRate < 0.75 ? (
                              <Chip
                                label="Occup."
                                size="small"
                                sx={{
                                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                  color: 'warning.main',
                                  fontWeight: 600,
                                }}
                              />
                            ) : (
                              <Chip
                                label="Active"
                                size="small"
                                sx={{
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  color: 'primary.main',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Paper>
              )}
            </Box>


          </>
        )}
      </Box>

      {/* Floating Action Button */}
      {!isNewUser && (
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
            }
          }}
          onClick={() => navigate('/add-hotel')}
        >
          <Add />
        </Fab>
      )}

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 180 }
        }}
      >
        <MenuItem onClick={() => handleFilterSelect('all')}>
          <Typography variant="body2">All Properties</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleFilterSelect('active')}>
          <Typography variant="body2">Active</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleFilterSelect('pending')}>
          <Typography variant="body2">Pending</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleFilterSelect('closed')}>
          <Typography variant="body2">Closed</Typography>
        </MenuItem>
      </Menu>

      {/* Global Styles */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default ValuationDashboard;
